import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { pendingInvite } from '@/utils/pendingInvite';
import { apiClient } from '@/services/api';
import { FormCard } from '@/components/ui/FormCard';
import { PasswordInput } from '@/components/PasswordInput';
import styles from './TelegramWelcomePage.module.scss';
import { VERBATIM_INPUT_PROPS } from '@/constants';

type Step = 'welcome' | 'linkEmail' | 'linkPassword' | 'optionalEmail' | 'bindEmail' | 'error';

const TelegramWelcomePage = () => {
  const { t } = useTranslation(['auth', 'common']);
  const navigate = useNavigate();
  const {
    telegramUserData,
    handleTelegramEmailSubmit,
    registerTelegramOnly,
    clearPendingTelegramWelcome,
  } = useAuth();

  const [step, setStep] = useState<Step>('welcome');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!telegramUserData) {
    navigate('/login', { replace: true });
    return null;
  }

  const handleCreateNew = () => {
    setError(null);
    setStep('optionalEmail');
  };

  const handleEmailCheck = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading || !email.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.checkEmail(email.trim().toLowerCase());
      const { exists, has_telegram } = response.data;

      if (exists && has_telegram) {
        setStep('error');
      } else if (exists) {
        setStep('linkPassword');
      } else {
        setError(t('auth:accountNotExist'));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('auth:errorCheckingEmail'));
    } finally {
      setLoading(false);
    }
  };

  const handleLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading || !password) return;

    setLoading(true);
    setError(null);

    try {
      await handleTelegramEmailSubmit(email.trim().toLowerCase(), password, telegramUserData, 'link');
      clearPendingTelegramWelcome();
      navigate(pendingInvite.resumePath('/'), { replace: true });
    } catch (err: any) {
      const code = err.response?.data?.code;
      if (code === 'TELEGRAM_CONFLICT') {
        setStep('error');
      } else {
        setError(err.response?.data?.message || t('auth:wrongPasswordOrLinkingError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    setLoading(true);
    setError(null);

    try {
      await registerTelegramOnly(telegramUserData);
      clearPendingTelegramWelcome();
      navigate(pendingInvite.resumePath('/'), { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.message || t('auth:errorCreatingAccount'));
    } finally {
      setLoading(false);
    }
  };

  const handleBindEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading || !email.trim() || !password) return;

    if (password !== passwordConfirm) {
      setError(t('auth:passwordsDoNotMatch'));
      return;
    }

    if (password.length < 6) {
      setError(t('auth:passwordMinLength'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await handleTelegramEmailSubmit(email.trim().toLowerCase(), password, telegramUserData, 'register');
      clearPendingTelegramWelcome();
      navigate(pendingInvite.resumePath('/'), { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.message || t('auth:errorCreatingAccount'));
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    setPassword('');
    setPasswordConfirm('');
    setError(null);

    if (step === 'bindEmail') {
      setEmail('');
      setStep('optionalEmail');
    } else if (step === 'optionalEmail') {
      setStep('welcome');
    } else if (step === 'linkPassword') {
      setStep('linkEmail');
    } else {
      setEmail('');
      setStep('welcome');
    }
  };

  const renderStepTitle = () => {
    switch (step) {
      case 'welcome': return t('auth:welcomeToPClash');
      case 'linkEmail': return t('auth:linkTelegram');
      case 'linkPassword': return t('auth:linkTelegram');
      case 'optionalEmail': return t('auth:bindEmailQuestion');
      case 'bindEmail': return t('auth:bindEmail');
      case 'error': return t('auth:linkingError');
    }
  };

  const renderStepSubtitle = () => {
    switch (step) {
      case 'welcome':
        return t('auth:chooseHowToContinue');
      case 'linkEmail':
        return t('auth:enterExistingEmail');
      case 'linkPassword':
        return t('auth:enterPasswordToLink');
      case 'optionalEmail':
        return t('auth:bindEmailExplanation');
      case 'bindEmail':
        return t('auth:enterEmailAndPassword');
      case 'error':
        return undefined;
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <FormCard
          title={renderStepTitle()}
          subtitle={renderStepSubtitle()}
          message={error ? { type: 'error', text: error } : null}
        >
          <div className={styles.telegramInfo}>
            <p>
              Telegram: <span className={styles.telegramName}>
                {telegramUserData.first_name} {telegramUserData.last_name}
              </span>
              {telegramUserData.username && (
                <span className={styles.telegramUsername}> (@{telegramUserData.username})</span>
              )}
            </p>
          </div>

          {step === 'welcome' && (
            <div className={styles.welcomeActions}>
              <button
                type="button"
                className="btn"
                onClick={handleCreateNew}
              >
                {t('auth:createNewAccount')}
              </button>
              <button
                type="button"
                className="btn btn--outline"
                disabled={loading}
                onClick={() => setStep('linkEmail')}
              >
                {t('auth:alreadyHaveAccountBtn')}
              </button>
            </div>
          )}

          {step === 'linkEmail' && (
            <form onSubmit={handleEmailCheck} className={styles.form}>
              <div className={styles.fieldGroup}>
                <label className={styles.label}>{t('auth:emailAddress')}</label>
                <input
                  type="email"
                  required
                  value={email}
                  autoComplete="email"
                  {...VERBATIM_INPUT_PROPS}
                  onChange={(e) => setEmail(e.target.value)}
                  className="field"
                  placeholder="your@email.com"
                  disabled={loading}
                  autoFocus
                />
              </div>
              <div className={styles.actions}>
                <button type="button" onClick={goBack} className="btn btn--outline" disabled={loading}>
                  {t('common:back')}
                </button>
                <button type="submit" disabled={loading} className="btn">
                  {loading ? t('auth:checking') : t('auth:continue')}
                </button>
              </div>
            </form>
          )}

          {step === 'linkPassword' && (
            <form onSubmit={handleLink} className={styles.form}>
              <div className={styles.fieldGroup}>
                <label className={styles.label}>{t('auth:email')}</label>
                <input
                  type="email"
                  value={email}
                  className="field"
                  disabled
                />
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.label}>{t('auth:passwordForExistingAccount')}</label>
                <PasswordInput
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('auth:enterPasswordToBind')}
                  minLength={6}
                  disabled={loading}
                  autoFocus
                />
              </div>
              <div className={styles.actions}>
                <button type="button" onClick={goBack} className="btn btn--outline" disabled={loading}>
                  {t('common:back')}
                </button>
                <button type="submit" disabled={loading} className="btn">
                  {loading ? t('auth:linking') : t('auth:linkAndLogIn')}
                </button>
              </div>
            </form>
          )}

          {step === 'optionalEmail' && (
            <div className={styles.welcomeActions}>
              <button
                type="button"
                className="btn"
                disabled={loading}
                onClick={() => {
                  setEmail('');
                  setPassword('');
                  setPasswordConfirm('');
                  setError(null);
                  setStep('bindEmail');
                }}
              >
                {t('auth:yesBindEmail')}
              </button>
              <button
                type="button"
                className="btn btn--outline"
                disabled={loading}
                onClick={handleSkip}
              >
                {loading ? t('auth:creating') : t('auth:noLater')}
              </button>
              <button
                type="button"
                className="btn btn--outline"
                disabled={loading}
                onClick={goBack}
              >
                {t('common:back')}
              </button>
            </div>
          )}

          {step === 'bindEmail' && (
            <form onSubmit={handleBindEmail} className={styles.form}>
              <div className={styles.fieldGroup}>
                <label className={styles.label}>{t('auth:emailAddress')}</label>
                <input
                  type="email"
                  required
                  value={email}
                  autoComplete="email"
                  {...VERBATIM_INPUT_PROPS}
                  onChange={(e) => setEmail(e.target.value)}
                  className="field"
                  placeholder="your@email.com"
                  disabled={loading}
                  autoFocus
                />
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.label}>{t('auth:password')}</label>
                <PasswordInput
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('auth:minimum6Chars')}
                  minLength={6}
                  disabled={loading}
                />
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.label}>{t('auth:confirmPassword')}</label>
                <PasswordInput
                  required
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  placeholder={t('auth:repeatPassword')}
                  minLength={6}
                  disabled={loading}
                />
              </div>
              <div className={styles.actions}>
                <button type="button" onClick={goBack} className="btn btn--outline" disabled={loading}>
                  {t('common:back')}
                </button>
                <button type="submit" disabled={loading} className="btn">
                  {loading ? t('auth:binding') : t('auth:bindEmail')}
                </button>
              </div>
            </form>
          )}

          {step === 'error' && (
            <div className={styles.errorContent}>
              <p className={styles.errorMessage}>
                {t('auth:emailAlreadyLinked')}
              </p>
              <button type="button" onClick={goBack} className="btn">
                {t('auth:tryDifferentEmail')}
              </button>
            </div>
          )}

          <div className={styles.terms}>
            <p>{t('auth:byAgreeing')}</p>
          </div>
        </FormCard>
      </div>
    </div>
  );
};

export default TelegramWelcomePage;
